import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { messageApi } from '@/api';
import { Button, Icon, LinkButton, Modal, Textarea, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';

const MAX_LENGTH = 4000;

/**
 * A student's private question to the company about a listing, before applying.
 *
 * One thread per student and listing: once asked, the button opens that
 * thread instead of starting another. The lookup's key sits under
 * `['conversations']`, so a reply arriving over the socket refreshes it.
 */
export function AskQuestionButton({ internship }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [sentId, setSentId] = useState(null);

  const existing = useQuery({
    queryKey: ['conversations', 'internship', internship.id],
    queryFn: () => messageApi.getInternshipQuestion(internship.id),
  });

  const ask = useMutation({
    mutationFn: () => messageApi.askAboutInternship(internship.id, body.trim()),
    onSuccess: (result) => {
      setSentId(result.conversation.id);
      setBody('');
      toast.success(`Question sent to ${internship.company.name}.`);
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['messages-unread'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  function close() {
    setOpen(false);
    setSentId(null);
  }

  // Kept on the modal while it is open: the refetch after sending finds the
  // new thread, and swapping the button then would unmount the dialog.
  if (existing.data && !open) {
    return (
      <LinkButton
        to={`${paths.student.messages}?c=${existing.data.id}`}
        variant="secondary"
        className="w-full"
      >
        <Icon name="chat" size={16} />
        View your question
      </LinkButton>
    );
  }

  return (
    <>
      <Button
        fullWidth
        variant="secondary"
        disabled={existing.isPending}
        onClick={() => setOpen(true)}
      >
        <Icon name="chat" size={16} />
        Ask a question
      </Button>

      <Modal
        open={open}
        title={sentId ? 'Question sent' : `Ask ${internship.company.name}`}
        description={
          sentId
            ? undefined
            : 'Only the company sees your question. You do not need to apply first.'
        }
        onClose={close}
        footer={
          sentId ? (
            <>
              <Button variant="secondary" onClick={close}>
                Close
              </Button>
              <LinkButton to={`${paths.student.messages}?c=${sentId}`}>
                Open conversation
              </LinkButton>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={close}>
                Cancel
              </Button>
              <Button
                isLoading={ask.isPending}
                disabled={!body.trim()}
                onClick={() => ask.mutate()}
              >
                <Icon name="send" size={16} />
                Send question
              </Button>
            </>
          )
        }
      >
        {sentId ? (
          <p className="text-ink-muted text-sm">
            It is in your messages. You will get a notification when {internship.company.name}{' '}
            replies.
          </p>
        ) : (
          <Textarea
            label="Your question"
            rows={5}
            maxLength={MAX_LENGTH}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder={`For example: Is the ${internship.title} role open to part-time students?`}
          />
        )}
      </Modal>
    </>
  );
}
